import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trophy, Download, ArrowRight, Package, Heart, Brain, Wind, Compass, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ReviewForm from "@/components/course/ReviewForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_ICONS = { "kit": Heart, "bundle": Package, "resources": Compass };

const CourseComplete = ({ email, lessons, progress }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [quizSummary, setQuizSummary] = useState(null);
  const [exporting, setExporting] = useState(false);

  const completedCount = Object.values(progress).filter((p) => p.video_watched && p.quiz_completed && p.exercise_completed).length;
  const isComplete = completedCount === lessons.length && lessons.length > 0;

  useEffect(() => {
    if (!isComplete || !email) return;
    let cancelled = false;
    const fetchRecs = async () => {
      try {
        const res = await axios.post(`${API}/course/recommendations`, { email });
        if (!cancelled) {
          setRecommendations(res.data.recommendations || []);
          setQuizSummary(res.data.quiz_summary);
        }
      } catch {
        toast.error("Could not load recommendations.");
      }
    };
    fetchRecs();
    return () => { cancelled = true; };
  }, [isComplete, email]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/course/export/${email}`);
      const data = res.data;

      let text = `BURNOUT TO BALANCE IN ACTION\nCourse Journal & Exercise Export\n`;
      text += `Student: ${data.email}\nExported: ${new Date(data.exported_at).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })}\n`;
      text += `${"=".repeat(60)}\n\n`;

      data.lessons.forEach((l) => {
        text += `LESSON ${l.lesson_id}: ${l.title}\n`;
        text += `${"-".repeat(40)}\n`;
        text += `Video: ${l.video_watched ? "Watched" : "Not watched"}\n`;
        text += `Quiz: ${l.quiz_completed ? `${l.quiz_score}/${l.quiz_total} correct` : "Not completed"}\n`;
        text += `Exercise: ${l.exercise_completed ? "Completed" : "Not completed"}`;
        if (l.exercise_title) text += ` (${l.exercise_title})`;
        text += `\n`;
        if (l.exercise_response) {
          try {
            const parsed = JSON.parse(l.exercise_response);
            text += `\nYour Responses:\n`;
            Object.entries(parsed).forEach(([key, val]) => {
              if (val && key !== "breathingDone") {
                const label = key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
                text += `  ${label}: ${val}\n`;
              }
            });
          } catch {
            text += `\nYour Response: ${l.exercise_response}\n`;
          }
        }
        text += `\n\n`;
      });

      text += `${"=".repeat(60)}\n`;
      text += `QUIZ SUMMARY: ${data.lessons.reduce((a, l) => a + (l.quiz_score || 0), 0)}/${data.lessons.reduce((a, l) => a + (l.quiz_total || 0), 0)} total correct\n`;
      text += `\nThis export was generated by The Lemonade Living Company.\n`;
      text += `Continue your journey at thelemonadelivingcompany.com\n`;

      const blob = new Blob([text], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `burnout-to-balance-journal-${new Date().toISOString().split("T")[0]}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export journal. Please try again.");
    }
    finally { setExporting(false); }
  };

  if (!isComplete) return null;

  return (
    <div data-testid="course-complete-section" className="mt-12 space-y-8">
      {/* Celebration Banner */}
      <div className="rounded-2xl p-8 md:p-12 text-center relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, rgba(247,208,70,0.15), rgba(163,184,153,0.2))", border: "1px solid var(--ll-border)" }}>
        <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
          style={{ background: "var(--ll-accent)" }}>
          <Trophy size={36} style={{ color: "var(--ll-text)" }} />
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light mb-3" style={{ color: "var(--ll-text)" }}>
          Course <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>Complete!</em>
        </h2>
        <p className="text-base max-w-md mx-auto mb-2" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
          Congratulations on completing Burnout To Balance In Action. You&apos;ve taken a powerful step toward lasting balance.
        </p>
        {quizSummary && (
          <div className="flex items-center justify-center gap-4 mt-4">
            <div className="px-4 py-2 rounded-xl" style={{ background: "white" }}>
              <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Quiz Score</p>
              <p className="font-serif text-lg font-semibold" style={{ color: "var(--ll-text)" }}>
                {quizSummary.score}/{quizSummary.total}
              </p>
            </div>
            <div className="px-4 py-2 rounded-xl" style={{ background: "white" }}>
              <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Lessons</p>
              <p className="font-serif text-lg font-semibold" style={{ color: "var(--ll-text)" }}>
                {lessons.length}/{lessons.length}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Export */}
      <div className="rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
        style={{ background: "white", border: "1px solid var(--ll-border)" }}>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--ll-bg-secondary)" }}>
            <Download size={18} style={{ color: "var(--ll-green)" }} />
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Export Your Journal</p>
            <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
              Download all your exercise responses, reflections, and quiz scores for personal review.
            </p>
          </div>
        </div>
        <button data-testid="export-journal-btn" onClick={handleExport} disabled={exporting}
          className="flex-shrink-0 rounded-full px-6 py-2.5 text-xs font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center gap-1.5 disabled:opacity-50"
          style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
          <Download size={12} /> {exporting ? "Preparing..." : "Download Journal"}
        </button>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-6">
            <Star size={18} style={{ color: "var(--ll-accent)" }} />
            <h3 className="font-serif text-xl font-medium" style={{ color: "var(--ll-text)" }}>
              Recommended For You
            </h3>
          </div>
          <div className="space-y-3">
            {recommendations.map((rec, i) => {
              const Icon = PRODUCT_ICONS[rec.product?.type] || Package;
              const isResources = rec.product_id === "resources";
              return (
                <div key={rec.product_id} data-testid={`recommendation-${rec.product_id}`}
                  className="rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-all duration-300 hover:-translate-y-0.5"
                  style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "var(--ll-bg-secondary)" }}>
                      <Icon size={18} style={{ color: "var(--ll-green)" }} />
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>
                        {rec.product?.name}
                      </p>
                      <p className="text-xs leading-relaxed mt-1" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                        {rec.reason}
                      </p>
                    </div>
                  </div>
                  <Link to={isResources ? "/resources" : "/#products"}
                    className="flex-shrink-0 rounded-full px-5 py-2 text-xs font-medium transition-all duration-300 hover:-translate-y-0.5 flex items-center gap-1.5"
                    style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
                    {isResources ? "Explore" : rec.product?.price > 0 ? `R${rec.product.price.toLocaleString()}` : "View"}
                    <ArrowRight size={12} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Review Form */}
      <ReviewForm email={email} />
    </div>
  );
};

export default CourseComplete;
